import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { loginAPI } from "../api/authApiService";
import { syncCookieDataToBackend } from "../api/cartWishlistSyncService";
import { showToast } from "../components/Toast";
import { setUserInfo } from "../store/authSlice";
import type { RootState } from "../store/store";
import { closeAuthModal, openCartDrawer } from "../store/uiSlice";
import { clearWishlist } from "../store/wishlistSlice";
import type { LoginData } from "../types/auth.types";
import { saveAuthCookies, saveUserId } from "../utilities/authUtils";
import { getCartItems, getWishlistItems, resolveUid } from "../utilities/cookieUtils";
import { clearRedirectAfterLogin, getRedirectAfterLogin } from "../utilities/redirectUtils";

export type SignInResult = { ok: true } | { ok: false; message: string };

// Never "return" a visitor to an auth route after they have signed in.
const AUTH_PAGES = ["/login", "/register", "/forgetpassword"];

/**
 * The one sign-in routine, shared by the login form and by registration's
 * automatic login: calls the login API, writes the session, moves the guest's
 * cookie cart/wishlist onto the account, closes the auth modal and then
 * finishes whatever sent the visitor here (open the cart, or go to the
 * protected page they were bounced from).
 *
 * Never throws. On failure it returns the message for the caller to show.
 */
export const useLoginSession = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { afterLogin, returnToCart } = useSelector(
    (state: RootState) => state.ui.authModal,
  );

  const signIn = useCallback(
    async (
      credentials: LoginData,
      successMessage = "Login successful!",
    ): Promise<SignInResult> => {
      let response;
      try {
        response = await loginAPI(credentials);
      } catch (error: any) {
        return {
          ok: false,
          message:
            error?.response?.data?.message ||
            "Something went wrong. Please try again.",
        };
      }

      const data = response?.data;
      if (!data?.token) {
        return { ok: false, message: response?.message || "Invalid login response" };
      }

      const { token, pksoft_token, userModel } = data;
      const userId = userModel?.code as string;

      /* ------------------------ SAVE COOKIES ------------------------ */
      // Capture guest cookies BEFORE resolveUid clears them, so the guest's
      // cart/wishlist can be synced to their account.
      const guestCart = getCartItems();
      const guestWishlist = getWishlistItems();

      saveAuthCookies(token, pksoft_token || "", userModel?.name || "", userId);
      saveUserId(userId);
      resolveUid();

      /* ------------------------ SYNC CART & WISHLIST ------------------------ */
      try {
        await syncCookieDataToBackend(userId, "user", guestCart, guestWishlist);
      } catch (err) {
        console.error("[LOGIN] Failed to sync cart/wishlist:", err);
      }

      /* ------------------------ REDUX STATE ------------------------ */
      dispatch(
        setUserInfo({
          username: userModel?.name,
          userid: userId,
          user_id: userId,
          token,
          pksoft_token,
        }),
      );
      dispatch(clearWishlist());
      // Also reopens the cart drawer if that's where the modal was opened from.
      dispatch(closeAuthModal());

      showToast({ type: "success", message: successMessage, timer: 2000 });

      /* ------------------------ FINISH THE CLICK ------------------------ */
      const redirectPath = getRedirectAfterLogin();
      clearRedirectAfterLogin();

      // (If the modal was opened from the cart drawer, closeAuthModal already
      // brought the drawer back — stay put.)
      if (afterLogin === "openCart") {
        dispatch(openCartDrawer());
      } else if (
        !returnToCart &&
        redirectPath &&
        !AUTH_PAGES.some((page) => redirectPath.startsWith(page))
      ) {
        navigate(redirectPath);
      }

      return { ok: true };
    },
    [dispatch, navigate, afterLogin, returnToCart],
  );

  return { signIn };
};

export default useLoginSession;
